'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { WidgetStyle } from '@/lib/chat-interface/widget-styles';

const LEGAL_URL = 'https://www.getlinkai.com/legal';
const CONSENT_KEY_PREFIX = 'linkai:legal-consent:';

/**
 * Shared consent state for the chat input.
 * Reads/writes acceptance to localStorage (per agent) so the notice only
 * appears once per user/agent. Guarded for SSR and sandboxed iframes.
 */
export function useLegalConsent(chatbotId: string) {
  const [mounted, setMounted] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const storageKey = `${CONSENT_KEY_PREFIX}${chatbotId}`;

  useEffect(() => {
    setMounted(true);
    try {
      setAccepted(window.localStorage.getItem(storageKey) === '1');
    } catch {
      // localStorage unavailable (private mode / sandboxed iframe) - show the card
      setAccepted(false);
    }
  }, [storageKey]);

  const accept = () => {
    setAccepted(true);
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      // Best-effort persistence only
    }
  };

  return { mounted, accepted, accept };
}

interface SurfaceProps {
  widgetStyle?: WidgetStyle;
  theme?: 'light' | 'dark';
}

function isDarkSurface(widgetStyle: WidgetStyle, theme: 'light' | 'dark') {
  return widgetStyle === 'black' || theme === 'dark';
}

function disclaimerClasses(widgetStyle: WidgetStyle, theme: 'light' | 'dark') {
  return cn(
    'text-[10px] text-center leading-relaxed',
    widgetStyle === 'black'
      ? 'text-neutral-400'
      : widgetStyle === 'glass'
        ? theme === 'dark'
          ? 'text-white/60'
          : 'text-neutral-500'
        : theme === 'dark'
          ? 'text-neutral-400'
          : 'text-neutral-500',
  );
}

interface LegalConsentCardProps extends SurfaceProps {
  mounted: boolean;
  accepted: boolean;
  onAccept: () => void;
  accentColor?: string;
}

/**
 * One-time legal/compliance notice shown ABOVE the chat input.
 * On "Accept and continue" it animates out (collapses upward) and the
 * companion `LegalDisclaimer` fades in below the input.
 */
export function LegalConsentCard({
  mounted,
  accepted,
  onAccept,
  widgetStyle = 'white',
  theme = 'light',
  accentColor,
}: LegalConsentCardProps) {
  const [exiting, setExiting] = useState(false);

  // Don't render for returning users (already accepted) or before hydration
  if (!mounted || accepted) {
    return null;
  }

  const dark = isDarkSurface(widgetStyle, theme);
  const buttonBg = accentColor || (dark ? '#ffffff' : '#000000');
  const buttonText = accentColor ? '#ffffff' : dark ? '#000000' : '#ffffff';

  const handleAccept = () => {
    // Play the collapse animation, then commit acceptance (which unmounts this
    // card and mounts the disclaimer under the input).
    setExiting(true);
    window.setTimeout(onAccept, 300);
  };

  return (
    <div
      className={cn(
        'px-0.5 transition-all duration-300 ease-out',
        exiting
          ? 'max-h-0 opacity-0 -translate-y-1 overflow-hidden pb-0'
          : 'max-h-48 opacity-100 translate-y-0 pb-2',
      )}
    >
      <div
        className={cn(
          'rounded-2xl border px-4 py-3',
          dark ? 'bg-white/10 border-white/15' : 'bg-black/5 border-black/10',
        )}
        role="alert"
      >
        <p className={cn('text-sm font-semibold', dark ? 'text-white' : 'text-neutral-900')}>
          Before you continue
        </p>
        <p className={cn('mt-1 text-xs leading-relaxed', dark ? 'text-white/70' : 'text-neutral-600')}>
          This AI can make mistakes and may collect information from your conversation to provide a
          better experience. Review our{' '}
          <a
            href={LEGAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'font-medium underline underline-offset-2',
              dark ? 'text-white' : 'text-neutral-900',
            )}
          >
            Privacy Policy and Terms
          </a>
          .
        </p>
        <button
          type="button"
          onClick={handleAccept}
          className="mt-3 w-full rounded-full py-2.5 text-sm font-medium transition-transform duration-150 ease-out hover:scale-[1.01] active:scale-[0.99]"
          style={{ backgroundColor: buttonBg, color: buttonText }}
        >
          Accept and continue
        </button>
      </div>
    </div>
  );
}

interface LegalDisclaimerProps extends SurfaceProps {
  mounted: boolean;
  accepted: boolean;
}

/**
 * Subtle disclaimer shown BELOW the chat input once the notice is accepted.
 * Fades/slides in when acceptance transitions from the card above.
 */
export function LegalDisclaimer({
  mounted,
  accepted,
  widgetStyle = 'white',
  theme = 'light',
}: LegalDisclaimerProps) {
  if (!mounted || !accepted) {
    return <div className="mt-2 px-1 pb-1" aria-hidden />;
  }

  return (
    <div className="mt-2 px-1 pb-1 animate-in fade-in slide-in-from-bottom-1 duration-300">
      <p className={disclaimerClasses(widgetStyle, theme)}>
        AI can make mistakes. Check important info.
      </p>
    </div>
  );
}
